/**
 * One definition of "where do we send someone who is not signed in, and how do we
 * get them back". Kept free of React and of the API client so both can import it.
 */

export const LOGIN_PATH = '/login'

/** Query parameter carrying the path the user was trying to reach. */
export const NEXT_PARAM = 'next'

type PathParts = { pathname: string; search?: string; hash?: string }

export function toPath(location: PathParts): string {
  return `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`
}

/** `/login?next=%2Fproducts%3Fpage%3D2` — never point `next` at /login itself. */
export function buildLoginPath(location: PathParts): string {
  const target = toPath(location)
  if (target === LOGIN_PATH || target.startsWith(`${LOGIN_PATH}?`)) return LOGIN_PATH
  return `${LOGIN_PATH}?${NEXT_PARAM}=${encodeURIComponent(target)}`
}

/**
 * Read the post-login destination back out. Anything that is not a same-origin
 * absolute path is discarded — an attacker-supplied `next=https://evil.example`
 * would otherwise turn the login screen into an open redirect.
 */
export function nextPathFrom(search: string, fallback = '/'): string {
  const raw = new URLSearchParams(search).get(NEXT_PARAM)
  if (!raw) return fallback

  const decoded = safeDecode(raw)
  const isSameOriginPath = decoded.startsWith('/') && !decoded.startsWith('//')
  if (!isSameOriginPath) return fallback
  if (decoded === LOGIN_PATH || decoded.startsWith(`${LOGIN_PATH}?`)) return fallback
  return decoded
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    // Malformed percent-encoding: treat as no destination at all.
    return ''
  }
}
